import { ArrowBack } from '@mui/icons-material';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { BackButtonConfig, BackButtonContext } from 'contexts';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import useBackButtonStyles from './styles';
import { BackButtonProps, BackButtonSearchParams } from './types';

const BackButton: React.FunctionComponent<BackButtonProps> = (props) => {
  const { isShown } = props;

  const classes = useBackButtonStyles(props);
  const { t } = useTranslation('common');
  const { spacing } = useTheme();
  const { backButton: backButtonProps } = useContext(BackButtonContext);

  const hiddenStyles = { height: 0, marginBottom: 0, overflow: 'hidden' };
  const shownStyles = { height: 'auto', marginBottom: spacing(0.5), overflow: 'visible' };

  const { search } = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const backButtonSearchConfig = useMemo(() => {
    if (!search) {
      return null;
    }
    const searchConfig: BackButtonConfig = { to: '', title: '' };
    searchParams.forEach((param, key) => {
      switch (key) {
        case BackButtonSearchParams.TITLE:
          searchConfig.title = param;
          break;
        case BackButtonSearchParams.TO:
          const paramValueArray = param.split('/').filter((value) => value !== '');
          searchConfig.to = `/${paramValueArray.join('/')}`;
          break;
      }
    });

    if (searchConfig.to === '' && searchConfig.title === '') {
      return null;
    }
    return searchConfig;
  }, [search, searchParams]);

  const initial = isShown ? shownStyles : hiddenStyles;
  const renderBackButton = () => (
    <Box sx={initial}>
      <Tooltip title={t('back')} placement='right'>
        <Link
          to={backButtonSearchConfig ? backButtonSearchConfig.to : backButtonProps.to}
          className={classes.backButton}
        >
          <ArrowBack className={classes.arrowBack} fontSize='small' />
          <Typography variant='subtitle2' className={classes.backButtonTitle}>
            {backButtonSearchConfig ? backButtonSearchConfig.title : backButtonProps.title}
          </Typography>
        </Link>
      </Tooltip>
    </Box>
  );

  return renderBackButton();
};

export default BackButton;
